import React, { useState, useEffect } from 'react';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import { subscribe } from 'react-contextual';

import { createSidebar } from '../../redux/actions/general/sidebar';
import {
  removePlusActions,
  setPlusActions,
} from '../../redux/actions/general/plus';
import {
  getStagesPermissions,
  listProjectSubstages,
  readStage,
  updateStageStatus,
  updateSubstageStatus,
} from '../../redux/actions/table/stages';
import {
  __,
  getActiveStage,
  redirect,
  setActiveProject,
  setTableParams,
} from '../../core/utils';
import {
  enableProjectsDropdown,
  listActiveModules,
} from '../../redux/actions/general/active_modules';
import styles from '../../styles/views/stages/Stages.scss';
import { withRouter } from 'react-router';
import Localization from '../../helpers/Localization';
import {
  readSubstage,
  updateSubstage,
} from '../../redux/actions/table/substages';
import { readProjectGeneral } from '../../redux/actions/projects';
import TitleBar from '../../containers/TitleBar';
import { setNotification } from '../../redux/actions/general/notification';
import { hideDropdown } from '../../redux/actions/general/dropdown';
import StagesFlyout from '../../flyouts/stages/StagesFlyout';
import SubstagesFlyout from '../../flyouts/substages/SubstagesFlyout';
import TableCompactView from './helpers/TableCompactView';
import TableListView from './helpers/TableListView';

const ProjectPlanning = ({
  actions,
  table,
  match,
  localization,
  auth,
  dropdownName,
  clicked_stage_row,
  clicked_substage_row,
  main_language,
}) => {
  const [expandedRows, setExpandedRows] = useState(null);
  const [focusedId, setFocusedId] = useState([]);
  const [isProjectAdministrator, setIsProjectAdministrator] = useState(false);
  const [hasPlanningModuleVisibility, setHasPlanningModuleVisibility] =
    useState(null);

  useEffect(() => {
    removePlusActions();
    setActiveProject(match.params.project);

    getStagesPermissions(match.params.project).then(({ response }) => {
      const is_project_administrator = _.get(response.data, 'administrator');
      const canViewProjectPlanning = _.includes(
        _.get(response.data, 'planning_module_visibility.permissions', []),
        'planning_module_visibility'
      );

      createSidebar({
        title: __('submenu.title.project'),
        items: _.compact([
          {
            title: __('submenu.title.stages'),
            link: () => '/projects/' + match.params.project + '/stages',
          },
          {
            title: __('submenu.title.teams'),
            link: () => '/stages/' + getActiveStage() + '/teams',
          },
          {
            title: __('submenu.title.approvers'),
            link: () => '/stages/' + getActiveStage() + '/approvers',
          },
          {
            title: __('submenu.title.project-properties'),
            link: () => '/projects/' + match.params.project + '/general',
          },
          {
            title: __('submenu.title.import_spreadsheet'),
            link: () => '/import/' + getActiveStage(),
          },
          canViewProjectPlanning && {
            title: __('submenu.title.project-planning'),
            link: () => '/projects/' + match.params.project + '/project-planning',
            active: true,
          },
        ]),
      });

      canViewProjectPlanning &&
        is_project_administrator &&
        setPlusActions([
          {
            name: __('wizard.add-stage.name.create-new-stage'),
            onClick: () =>
              actions.setTableParams({
                wizard_active: true,
              }),
          },
          {
            name: __('wizard.add-stage.name.create-new-substage'),
            onClick: () => {
              setTableParams('substages', {
                wizard_active: true,
              });
            },
            disabled: _.size(table.data) > 0,
          },
        ]);

      setHasPlanningModuleVisibility(canViewProjectPlanning);
      setIsProjectAdministrator(is_project_administrator);

      canViewProjectPlanning && listProjectSubstages(match.params.project);
    });

    return () => {
      removePlusActions();
    };
  }, [match.params.project]);

  useEffect(() => {
    if (expandedRows !== null || _.isEmpty(table?.substages)) {
      return;
    }

    const defaultExpanded = _.reduce(
      table.substages,
      (ids, group) => {
        !_.isEmpty(group.children) && ids.push(group.id);
        return ids;
      },
      []
    );

    setExpandedRows(defaultExpanded);
  }, [expandedRows, table?.substages]);

  useEffect(() => {
    listActiveModules();
    setActiveProject(match.params.project);

    enableProjectsDropdown(({ project_id }) => {
      getStagesPermissions(project_id).then(({ response }) => {
        const canViewProjectPlanning = _.includes(
          _.get(response.data, 'planning_module_visibility.permissions', []),
          'planning_module_visibility'
        );

        redirect(
          '/projects/' +
            project_id +
            (canViewProjectPlanning ? '/project-planning' : '/stages')
        );
      });
    });

    readProjectGeneral(match.params.project).then(({ response }) => {
      setTableParams('stages', {
        main_language: response.data.main_language,
        project_id: match.params.project,
      });

      listActiveModules();
    });

  }, [match.params.project]);

  const _updateSubstage = (id, data) => {
    updateSubstage(id, data).then(() => {
      listProjectSubstages(match.params.project);
      setNotification(__('project.substages.notification.success-update'));
      hideDropdown();
    });
  };

  const _setStageStatus = (id, status) => {
    updateStageStatus(id, status).then(() => {
      listProjectSubstages(match.params.project);

      setNotification(__('project.stages.notification.success-update'));

      hideDropdown();
    });
  };

  const _setSubstageStatus = (id, status) => {
    updateSubstageStatus(id, status).then(() => {
      listProjectSubstages(match.params.project);

      setNotification(__('project.substages.notification.success-update'));

      hideDropdown();
    });
  };

  const main_lanugage = main_language;
  const activeView = _.get(table, 'view', 'compact');

  return (
    <div className={styles.table}>
      <TitleBar
        title={__('submenu.title.project-planning')}
        dropdownHeader={__('submenu.title.project')}
        toolbar
      />

      <div className={styles.tableContent}>
        {hasPlanningModuleVisibility === false && (
          <div className={styles.noPermissionMessage}>
            {__('general.no-permission-to-access')}
          </div>
        )}
        {hasPlanningModuleVisibility && activeView === 'compact' && (
          <TableCompactView
            name='stages'
            title={__('submenu.title.project-planning')}
            expandedRows={expandedRows}
            localization={localization}
            data={table?.substages}
            listProjectSubstages={() => {
              listProjectSubstages(match.params.project);
            }}
            onExpandedRowsChange={(expanded_rows) => {
              setExpandedRows(expanded_rows);
            }}
            isProjectAdministrator={isProjectAdministrator}
            onFocusChange={setFocusedId}
            focusedId={focusedId}
            languageId={main_lanugage}
            disabledColumns={table?.disabled_columns || []}
            dropdownName={dropdownName}
            onDisabledColumnsChange={(disabled_columns) =>
              actions.setTableParams({ disabled_columns })
            }
            onStatusChange={(level, id, data) => {
              if (level == 0) return _setStageStatus(id, data.status);

              return _setSubstageStatus(id, data.status);
            }}
            onSubstageUpdate={(id, data) => _updateSubstage(id, data)}
          />
        )}
        {hasPlanningModuleVisibility && activeView === 'list' && (
          <TableListView
            name='stages'
            title={__('submenu.title.project-planning')}
            expandedRows={expandedRows}
            localization={localization}
            data={table?.substages}
            listProjectSubstages={() => {
              listProjectSubstages(match.params.project);
            }}
            onExpandedRowsChange={(expanded_rows) => {
              setExpandedRows(expanded_rows);
            }}
            isProjectAdministrator={isProjectAdministrator}
            onFocusChange={setFocusedId}
            focusedId={focusedId}
            languageId={main_lanugage}
            disabledColumns={table?.disabled_columns || []}
            dropdownName={dropdownName}
            onDisabledColumnsChange={(disabled_columns) =>
              actions.setTableParams({ disabled_columns })
            }
            onStatusChange={(level, id, data) => {
              if (level == 0) return _setStageStatus(id, data.status);

              return _setSubstageStatus(id, data.status);
            }}
            onSubstageUpdate={(id, data) => _updateSubstage(id, data)}
          />
        )}
      </div>

      {hasPlanningModuleVisibility && clicked_stage_row && (
        <div className={styles.flyout}>
          <StagesFlyout
            data={table.flyout}
            is_project_administrator={isProjectAdministrator}
            readAction={(stage_id) => {
              return new Promise(() => {
                readStage(stage_id);
                listProjectSubstages(match.params.project);
              });
            }}
          />
        </div>
      )}

      {hasPlanningModuleVisibility && clicked_substage_row && (
        <div className={styles.flyout}>
          <SubstagesFlyout
            language_id={main_lanugage}
            data={table.substage_flyout}
            is_project_administrator={isProjectAdministrator}
            readAction={(substage_id) => {
              return new Promise(() => {
                readSubstage(substage_id).then(({ response }) => {
                  setTableParams('stages', {
                    substage_flyout: response.data,
                  });
                });

                listProjectSubstages(match.params.project);
              });
            }}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    table: state.table['stages'],
    main_language: state.table['stages'].main_language,
    auth: state.auth,
    dropdownName: state.general.dropdown.shown,
    clicked_stage_row: _.get(state.table, [
      'stages',
      'clicked_stage_row',
    ]),
    clicked_substage_row: _.get(state.table, [
      'stages',
      'clicked_substage_row',
    ]),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: {
      setTableParams: (params) =>
        dispatch({
          type: 'table.stages/SET_TABLE_PARAMS',
          params,
        }),
    },
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(subscribe(Localization, 'localization')(ProjectPlanning))
);
